// frontend/src/pages/Finance.js
//
// 💶 Pénzügy — pályázati beadáshoz. Burn rate, runway, MRR/ARR, készpénz, EUR-ban.
//
// A lap szerkesztési elve: MINDEN szám mellett ott a képlet és a forrás. Egy
//   pályázatban a szám önmagában nem ér semmit — azt kell tudni megvédeni,
//   hogyan jött ki. Ezért a kártyák alatt mindig ott a „hogyan számoltuk" sor,
//   és a bank alapú / mért burn külön, egymás mellett látszik az eltéréssel.

import React, { useEffect, useState, useCallback } from 'react';

const C = {
  bg: '#1a1d27', panel: '#22252f', border: '#2a2e3a', text: '#e0e6ed',
  muted: '#8892a0', accent: '#00d4ff', warn: '#fbbf24', ok: '#22c55e', bad: '#ef4444',
};

const eur = (v, d = 0) => (v == null ? '—' : new Intl.NumberFormat('hu-HU', {
  style: 'currency', currency: 'EUR', minimumFractionDigits: d, maximumFractionDigits: d,
}).format(v));
const num = (v, d = 1) => (v == null ? '—' : new Intl.NumberFormat('hu-HU', {
  minimumFractionDigits: d, maximumFractionDigits: d,
}).format(v));

const card = { background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: 16 };
const input = {
  background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, color: C.text,
  padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%',
};
const btn = (primary) => ({
  padding: '8px 14px', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: primary ? 700 : 400,
  border: primary ? 'none' : '1px solid ' + C.border,
  background: primary ? C.accent : C.bg, color: primary ? '#0a0e1a' : C.text,
});
const th = { textAlign: 'left', padding: '8px 10px', color: C.muted, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 12, borderTop: '1px solid ' + C.border, whiteSpace: 'nowrap' };

// Nagy szám + alatta a képlet. A képlet nem dísz: enélkül a szám nem védhető.
function Metric({ label, value, sub, formula, tone }) {
  return (
    <div style={{ ...card, flex: '1 1 200px', minWidth: 200 }}>
      <div style={{ color: C.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tone || C.text, margin: '6px 0 2px' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: C.text }}>{sub}</div>}
      {formula && <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>{formula}</div>}
    </div>
  );
}

export default function Finance() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await fetch('/api/finance/summary', { credentials: 'include' });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status);
      setD(j);
    } catch (e) { setErr(e.message); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const send = async (path, body, method = 'POST') => {
    setSaving(true);
    try {
      const r = await fetch('/api/finance' + path, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status);
      await load();
      return true;
    } catch (e) { alert('Hiba: ' + e.message); return false; } finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24, color: C.muted }}>Betöltés…</div>;
  if (err) return <div style={{ padding: 24, color: C.bad }}>Hiba: {err}</div>;
  if (!d) return null;

  const B = d.burn, R = d.runway, V = d.revenue, Cash = d.cash;
  const basisLabel = B.basis === 'bank' ? 'bank alapú' : 'mért';

  return (
    <div style={{ padding: 20, color: C.text, maxWidth: 1320 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>💶 Pénzügy — pályázati adatok</h1>
          <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
            Állapot: {d.asOfDate} · utolsó teljes hónap: <b>{d.upToMonth}</b> · minden érték euróban
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} style={btn(false)}>↻ Frissítés</button>
          <a href="/api/finance/export" style={{ ...btn(true), textDecoration: 'none', display: 'inline-block' }}>⬇️ xlsx export</a>
          <a href="/api/finance/export.pdf" style={{ ...btn(true), textDecoration: 'none', display: 'inline-block' }}
            title={d.missing.length ? 'Amíg bemenet hiányzik, a PDF MUNKAVÁLTOZATKÉNT készül' : 'Pályázati melléklet PDF-ben'}>
            ⬇️ PDF{d.missing.length ? ' (munkaváltozat)' : ''}
          </a>
        </div>
      </div>

      {/* Amit még pótolni kell — ez a lap legfontosabb doboza, ezért felül van. */}
      {d.missing.length > 0 && (
        <div style={{ ...card, borderColor: C.warn, marginTop: 14 }}>
          <div style={{ color: C.warn, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>
            ⚠️ Ezek nélkül a számok nem beadhatók
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, color: C.text, fontSize: 12, lineHeight: 1.7 }}>
            {d.missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* Fejléc-mutatók */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
        <Metric
          label="Készpénzpozíció"
          value={eur(Cash ? Cash.eur : null)}
          tone={Cash ? C.text : C.bad}
          sub={Cash
            ? (Cash.accounts && Cash.accounts.length > 1
              ? `${Cash.accounts.map((a) => `${num(a.amount, 0)} ${a.currency}`).join(' + ')} · ${Cash.asOfDate}`
              : `${num(Cash.amount, 0)} ${Cash.currency} · ${Cash.asOfDate}`)
            : 'nincs felvive'}
          formula={Cash
            ? `Bankszámlakivonat egyenlege${Cash.source ? ` (${Cash.source})` : ''}, átváltva: ${Cash.fxUsed ? Cash.fxUsed.source : 'ismeretlen árfolyam'}`
            : 'A kivonatról kell átvenni — nem származtatjuk semmiből'}
        />
        <Metric
          label={`Havi burn rate (${basisLabel})`}
          value={eur(B.headlinePerMonthEur)}
          tone={C.warn}
          sub={B.basis === 'bank' && B.bank ? `${B.bank.from} → ${B.bank.to}` : (B.measured ? B.measured.months.join(', ') : '')}
          formula={B.basis === 'bank'
            ? 'Nyitó egyenleg − záró egyenleg + nem működési tételek, napra súlyozva'
            : '(változó + fix költség) − nettó bevétel, havi átlag'}
        />
        <Metric
          label="Runway"
          value={R.profitable ? 'nem fogy'
            : R.beyondHorizon ? '50+ év'
              : (R.months == null ? '—' : num(R.months, 1) + ' hónap')}
          tone={R.unstable ? C.warn : R.profitable || R.beyondHorizon ? C.ok : (R.months != null && R.months < 6 ? C.bad : C.text)}
          sub={R.unstable ? '⚠️ nem stabil — ne közöld, lásd lent' : R.endDate ? `Kifogyás: ${R.endDate}` : (R.reason || '')}
          formula={`Készpénz / havi burn (${basisLabel})`}
        />
        <Metric
          label="MRR"
          value={V ? eur(V.mrrEur) : '—'}
          tone={V && V.mrrIsMeaningful ? C.text : C.muted}
          sub={V ? `ARR: ${eur(V.arrEur)}` : ''}
          formula={V && V.mrrIsMeaningful ? 'Előfizetéses termékek nettó havi bevétele × 12' : 'Nincs előfizetéses bevétel — lásd lent'}
        />
        <Metric
          label="Annualizált futóráta"
          value={V ? eur(V.runRateFromLastMonthEur) : '—'}
          sub={V ? `${V.window} havi átlagból: ${eur(V.runRateFromAvgEur)}` : ''}
          formula="Utolsó teljes hónap nettó bevétele × 12 — NEM szerződött ismétlődő bevétel"
        />
      </div>

      {/* 🔴 Ha nincs előfizetés, az MRR nem 0 „egyelőre", hanem értelmezhetetlen. */}
      {V && !V.mrrIsMeaningful && (
        <div style={{ ...card, borderColor: C.bad, marginTop: 12 }}>
          <div style={{ color: C.bad, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
            🔴 MRR/ARR néven ne adj be semmit
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.7 }}>{V.mrrCaveat}</div>
          <div style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, color: C.muted }}>
            Helyette ez a mondat védhető: „{d.upToMonth} havi nettó bevétel {eur(V.netRevenueLastMonthEur, 2)},
            ami {eur(V.runRateFromLastMonthEur)} annualizált futórátának felel meg (egyszeri, tranzakciós
            bevétel, nem szerződött ismétlődő bevétel)."
          </div>
        </div>
      )}

      {/* A két burn egymás mellett + az eltérés */}
      <div style={{ ...card, marginTop: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>A burn rate két független úton</div>
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 12, lineHeight: 1.6 }}>
          A bank alapú szám a beadott kivonatokból SZERKEZETÉNÉL FOGVA kijön — a bíráló is ezt tudja
          kiszámolni. A mért szám tételes és magyarázható, de csak azt látja, amiről a dashboardnak
          tudomása van. A kettő különbsége az, amit nem látunk.
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Bank alapú</div>
            <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0' }}>{eur(B.bank ? B.bank.burnPerMonthEur : null)}<span style={{ fontSize: 13, color: C.muted }}> /hó</span></div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              {B.bank ? `${B.bank.from} → ${B.bank.to} · ${B.bank.days} nap · ${B.bank.window} időszak` : 'Legalább két hónapvégi egyenleg kell hozzá'}
            </div>
          </div>
          <div style={{ flex: '1 1 260px', background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 12 }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Mért (tételes)</div>
            <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0' }}>{eur(B.measured ? B.measured.netBurnEur : null)}<span style={{ fontSize: 13, color: C.muted }}> /hó</span></div>
            <div style={{ fontSize: 11, color: B.measured && !B.measured.complete ? C.warn : C.muted, lineHeight: 1.6 }}>
              {B.measured
                ? `${B.measured.months.join(', ')}` + (B.measured.complete ? '' : ` · ⚠️ hiányos költségadat (${B.measured.incompleteMonths.join(', ')}) → alsó becslés`)
                : 'Nincs elég adat'}
            </div>
          </div>
          <div style={{
            flex: '1 1 260px', background: C.bg, borderRadius: 8, padding: 12,
            border: '1px solid ' + (B.reconciliation.comparable && !B.reconciliation.consistent ? C.warn : C.border),
          }}>
            <div style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase' }}>Eltérés</div>
            <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0', color: B.reconciliation.comparable && !B.reconciliation.consistent ? C.warn : C.text }}>
              {B.reconciliation.comparable
                ? eur(B.reconciliation.differenceEur) + (B.reconciliation.percentMeaningful ? ` (${num(B.reconciliation.differencePct, 1)}%)` : '')
                : '—'}
            </div>
            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.6 }}>
              {B.reconciliation.comparable
                ? (B.reconciliation.consistent
                  ? 'A két szám ugyanazt a valóságot írja le.'
                  : 'Ez a pénzmozgás nem szerepel a dashboard költségadataiban — vidd fel fix költségként.')
                  + (B.reconciliation.percentNote ? ' ' + B.reconciliation.percentNote : '')
                : B.reconciliation.reason}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: C.muted }}>A runway alapja:</span>
          {[['bank', 'bank alapú'], ['measured', 'mért']].map(([k, l]) => (
            <button key={k} disabled={saving} onClick={() => send('/settings', { burnBasis: k })}
              style={{ ...btn(B.basis === k), opacity: saving ? 0.5 : 1 }}>{l}</button>
          ))}
          <span style={{ fontSize: 11, color: C.muted }}>
            → {R.profitable ? 'nem fogy' : R.beyondHorizon ? '50+ év' : num(R.months, 1) + ' hónap'}
            {R.alternatives && (
              <> · a másikkal: {(() => {
                const alt = B.basis === 'bank' ? R.alternatives.measured : R.alternatives.bank;
                return alt.profitable ? 'nem fogy' : alt.beyondHorizon ? '50+ év' : (alt.months == null ? '—' : num(alt.months, 1) + ' hónap');
              })()}</>
            )}
          </span>
        </div>
      </div>

      {/* Runway feltételezések — pályázatban ezt kérdezik vissza */}
      <div style={{ ...card, marginTop: 12 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>A runway feltételezései</div>
        {R.unstable && (
          <div style={{ color: C.warn, fontSize: 12, lineHeight: 1.6, marginBottom: 10, fontWeight: 600 }}>
            ⚠️ {R.unstableReason}
          </div>
        )}
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.8, color: C.text }}>
          {R.assumptions.map((a, i) => <li key={i}>{a}</li>)}
        </ul>
      </div>

      {/* Fülek */}
      <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
        {[
          ['overview', '📊 Havi bontás'],
          ['cash', '🏦 Bankegyenlegek'],
          ['actuals', '🧾 Tényleges kifizetések'],
          ['fixed', '💸 Fix költségek'],
          ['fx', '💱 Árfolyam'],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={btn(tab === k)}>{l}</button>
        ))}
      </div>

      <div style={{ ...card, marginTop: 10, overflowX: 'auto' }}>
        {tab === 'overview' && <MonthlyTable d={d} />}
        {tab === 'cash' && <CashTab d={d} send={send} saving={saving} />}
        {tab === 'actuals' && <ActualsTab d={d} send={send} saving={saving} />}
        {tab === 'fixed' && <FixedTab d={d} send={send} saving={saving} />}
        {tab === 'fx' && <FxTab d={d} send={send} saving={saving} load={load} />}
      </div>

      {d.problems.length > 0 && (
        <div style={{ ...card, marginTop: 12, borderColor: C.bad }}>
          <div style={{ color: C.bad, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Számítás közbeni problémák</div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.7, color: C.muted }}>
            {[...new Set(d.problems)].map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Havi bontás ────────────────────────────────────────────────────────────
function MonthlyTable({ d }) {
  return (
    <>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.6 }}>
        A folyó hónap részleges, ezért szerepel a táblában, de az átlagokból kimarad.
        A „Napok" oszlop azt mutatja, hány napra van egyáltalán költségadat — ha kevés,
        az adott hónap költsége alsó becslés.
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Hónap', 'Változó ktg.', 'Fix ktg.', 'Összes ktg.', 'Bruttó bev.', 'Nettó bev.', 'Nettó alapja', 'ebből előfiz.', 'Nettó burn', 'Napok', 'Árfolyam'].map((h) => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {d.series.map((s) => {
            const partial = s.month > d.upToMonth;
            return (
              <tr key={s.month} style={{ opacity: partial ? 0.55 : 1 }}>
                <td style={{ ...td, fontWeight: 600 }}>
                  {s.month}{partial && <span style={{ color: C.warn, fontSize: 10 }}> részleges</span>}
                </td>
                <td style={td}>{eur(s.variableCostEur, 2)}</td>
                <td style={td}>{eur(s.fixedCostEur, 2)}</td>
                <td style={td}>{eur(s.totalCostEur, 2)}</td>
                <td style={td}>{eur(s.revenueGrossEur, 2)}</td>
                <td style={td}>{eur(s.revenueNetEur, 2)}</td>
                <td style={{ ...td, color: s.revenueNetBasis === 'modelled' ? C.muted : C.ok, fontSize: 11 }}>
                  {{ actual: '✅ tény', modelled: 'modell', mixed: '◐ vegyes' }[s.revenueNetBasis] || '—'}
                </td>
                <td style={td}>{eur(s.recurringNetEur, 2)}</td>
                <td style={{ ...td, color: s.netBurnEur > 0 ? C.warn : C.ok, fontWeight: 600 }}>{eur(s.netBurnEur, 2)}</td>
                <td style={{ ...td, color: s.costDataComplete ? C.muted : C.warn }}>
                  {s.costDaysWithData}/{s.daysInMonth}{!s.costDataComplete && ' ⚠️'}
                </td>
                <td style={{ ...td, color: C.muted, fontSize: 11 }}>
                  {s.fxUsed ? (s.fxUsed.source || '—') + (s.fxUsed.approximated ? ` (${s.fxUsed.forMonth})` : '') : 'nincs'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// ── Bankegyenlegek ─────────────────────────────────────────────────────────
function CashTab({ d, send, saving }) {
  const [f, setF] = useState({ as_of_date: '', account: 'HUF', currency: 'HUF', amount: '', non_operating_in: '', non_operating_out: '', source: '', note: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const ok = await send('/cash-balances', {
      ...f,
      amount: Number(f.amount),
      non_operating_in: Number(f.non_operating_in || 0),
      non_operating_out: Number(f.non_operating_out || 0),
    });
    if (ok) setF({ ...f, as_of_date: '', amount: '', non_operating_in: '', non_operating_out: '', source: '', note: '' });
  };

  return (
    <>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.7 }}>
        Ezt a számot a beadott bankszámlakivonat igazolja, ezért <b>nem származtatjuk semmiből</b> — a
        kivonatról kell átvenni. Vidd fel mind a 6 hónap végét: abból a bank alapú burn ugyanazon az
        adaton nyugszik, amit beadtál.
        <br />
        A <b>nem működési</b> oszlopok az ADOTT DÁTUMIG tartó időszakra vonatkoznak: tőkeemelés, hitel,
        pályázati előleg → „be"; osztalék, hiteltörlesztés → „ki". Ezek nélkül egy tőkeemelés úgy
        látszana, mintha nyereség lenne, és a burn hamisan alacsony lenne.
        <br />
        Ha <b>több bankszámlád van</b> (pl. forint és deviza), mindegyiket külön vidd fel ugyanarra a
        dátumra, saját néven. A burn számlánként, a saját pénznemében képződik, és csak utána adódik
        össze euróban — így a két számla közti átvezetés kiejti magát, nem látszik bevételnek.
        Ha egy számla egyenlege hiányzik egy dátumnál, a lap szól: enélkül a készpénz alulmutatna.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ flex: '0 0 140px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Dátum (hónap vége)</div>
          <input type="date" value={f.as_of_date} onChange={set('as_of_date')} style={input} />
        </label>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Számla</div>
          <input value={f.account} onChange={set('account')} placeholder="HUF" style={input} />
        </label>
        <label style={{ flex: '0 0 150px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Egyenleg</div>
          <input type="number" step="0.01" value={f.amount} onChange={set('amount')} placeholder="4200000" style={input} />
        </label>
        <label style={{ flex: '0 0 90px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Pénznem</div>
          <select value={f.currency} onChange={set('currency')} style={input}>
            {['HUF', 'EUR', 'USD'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 0 140px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Nem működési be</div>
          <input type="number" step="0.01" value={f.non_operating_in} onChange={set('non_operating_in')} placeholder="0" style={input} />
        </label>
        <label style={{ flex: '0 0 140px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Nem működési ki</div>
          <input type="number" step="0.01" value={f.non_operating_out} onChange={set('non_operating_out')} placeholder="0" style={input} />
        </label>
        <label style={{ flex: '1 1 200px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Forrás (melyik kivonat)</div>
          <input value={f.source} onChange={set('source')} placeholder="bankszámlakivonat 2026-08" style={input} />
        </label>
        <button onClick={submit} disabled={saving || !f.as_of_date || f.amount === ''} style={btn(true)}>Mentés</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Dátum', 'Számla', 'Egyenleg', 'EUR', 'Nem műk. be', 'Nem műk. ki', 'Forrás', ''].map((h) => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {d.balances.slice().reverse().map((b) => (
            <tr key={b.asOfDate + '|' + b.account}>
              <td style={{ ...td, fontWeight: 600 }}>{b.asOfDate}</td>
              <td style={{ ...td, color: C.accent }}>{b.account}</td>
              <td style={td}>{num(b.amount, 2)} {b.currency}</td>
              <td style={td}>{eur(b.eur)}</td>
              <td style={td}>{b.nonOperatingInEur ? eur(b.nonOperatingInEur) : '—'}</td>
              <td style={td}>{b.nonOperatingOutEur ? eur(b.nonOperatingOutEur) : '—'}</td>
              <td style={{ ...td, color: b.source ? C.text : C.warn, whiteSpace: 'normal' }}>{b.source || 'nincs megadva'}</td>
              <td style={td}>
                <button onClick={() => window.confirm(`Törlöd a ${b.asOfDate} / ${b.account} egyenleget?`) && send(`/cash-balances/${b.id}`, null, 'DELETE')}
                  style={{ ...btn(false), padding: '4px 8px', color: C.bad }}>×</button>
              </td>
            </tr>
          ))}
          {!d.balances.length && <tr><td colSpan={8} style={{ ...td, color: C.muted }}>Még nincs felvive egyenleg.</td></tr>}
        </tbody>
      </table>

      {d.bankPeriods.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: 13, margin: '18px 0 6px' }}>Időszakok — innen jön a bank alapú burn</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.6 }}>
            A különbséget a számla saját pénznemében képezzük, és csak a kész burn-t váltjuk euróra.
            Az „Árfolyamhatás" oszlop azt mutatja, mennyit tett volna hozzá tévesen az, ha előbb
            váltanánk át mindkét egyenleget — ez nem pénzmozgás, hanem az árfolyam elmozdulása.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {['Időszak', 'Nap', 'Nyitó EUR', 'Záró EUR', 'Számlánként', 'Burn EUR', 'EUR/hó', 'Árfolyamhatás'].map((h) => <th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {d.bankPeriods.map((p) => (
                <tr key={p.from + p.to}>
                  <td style={td}>{p.from} → {p.to}{!p.complete && <span style={{ color: C.warn }}> ⚠️</span>}</td>
                  <td style={td}>{p.days}</td>
                  <td style={td}>{eur(p.openingEur)}</td>
                  <td style={td}>{eur(p.closingEur)}</td>
                  <td style={{ ...td, color: C.muted, fontSize: 11, whiteSpace: 'normal' }}>
                    {(p.accounts || []).map((a) => `${a.account}: ${num(a.burnNative, 0)} ${a.currency}`).join(' · ')}
                  </td>
                  <td style={td}>{eur(p.burnEur)}</td>
                  <td style={{ ...td, fontWeight: 600, color: p.burnEur > 0 ? C.warn : C.ok }}>{eur(p.burnPerMonthEur)}</td>
                  <td style={{ ...td, color: C.muted }}>{p.fxEffectEur == null ? '—' : eur(p.fxEffectEur, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// ── Tényleges bolti kifizetések ────────────────────────────────────────────
// Ez a lap hiteles forrás: a bolt által ténylegesen átutalt összeg, számlával.
//   Ahol van ilyen sor, az felülírja a jutalékkulcsból modellezett értéket.
const SOURCE_LABELS = {
  ios_iap: '🍎 Apple (EUR)', android_iap: '🤖 Google Play (HUF)',
  b2b_sponsor: '🤝 B2B szponzor', other: 'Egyéb',
};

function ActualsTab({ d, send, saving }) {
  const empty = { month: '', source: 'android_iap', amount: '', currency: 'HUF', invoice_ref: '', note: '' };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => {
    const v = e.target.value;
    // A bolt pénzneme adott: az Apple euróban, a Google forintban utal. A
    //   forrásváltás ezért átállítja a pénznemet is — de felülírható marad.
    if (k === 'source') {
      setF({ ...f, source: v, currency: v === 'ios_iap' ? 'EUR' : v === 'android_iap' ? 'HUF' : f.currency });
    } else setF({ ...f, [k]: v });
  };

  const submit = async () => {
    const ok = await send('/revenue-actuals', { ...f, amount: Number(f.amount) });
    if (ok) setF({ ...empty, source: f.source, currency: f.currency });
  };

  return (
    <>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.7 }}>
        Az az összeg, amit a bolt <b>ténylegesen átutalt</b> — a Google/Apple felé kiállított számlád
        szerint. Ahol ez fel van víve, a nettó bevétel <b>ez</b>, nem a bruttóból, feltételezett
        jutalékkulccsal számolt érték. A jutalékkulcs feltételezés; a kifizetést számla igazolja.
        <br />
        Az összeget <b>a kifizetés saját pénznemében</b> írd be (Apple: EUR, Google Play: HUF) —
        nem kell átváltani.
        <br />
        Egy hónapra <b>több számla is mehet</b> ugyanattól a bolttól; ezek összeadódnak, és minden
        szám mögött megmarad a konkrét számlaszám. Ugyanazt a számlaszámot viszont nem engedi
        kétszer bevinni, mert az duplázná a bevételt.
      </div>

      {/* InforEuro automatikusan — a szerver naponta maga is lefuttatja */}
      <div style={{ ...card, background: C.bg, marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>🇪🇺 InforEuro — automatikus letöltés</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.6 }}>
              A szerver naponta letölti a Bizottság havi árfolyamát (EUR/HUF, EUR/USD), és kitölti a hiányzó
              hónapokat. Meglévő értéket nem ír felül — ha eltér a hivatalostól, szól.
            </div>
          </div>
          <button onClick={fetchOfficial} disabled={fetching} style={btn(true)}>
            {fetching ? '⏳ Letöltés…' : '🔄 Letöltés most'}
          </button>
        </div>
        {fetched && fetched.error && (
          <div style={{ color: C.bad, fontSize: 12, marginTop: 10 }}>
            ❌ {fetched.error}
            <div style={{ color: C.muted, marginTop: 4 }}>
              Ha a szerver nem éri el az InforEuro API-t, a lenti beillesztés továbbra is működik.
            </div>
          </div>
        )}
        {fetched && !fetched.error && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, color: fetched.status === 'ok' ? C.ok : C.warn, marginBottom: 6 }}>
              {fetched.status === 'ok' ? '✅' : '⚠️'} {fetched.filled} árfolyam kitöltve ({fetched.from} óta)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Hónap', 'EUR/HUF', 'EUR/USD', 'Eredmény'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {fetched.report.map((r) => {
                  const act = r.actions || {};
                  const label = r.status === 'not_published' ? 'még nincs kihirdetve'
                    : r.status === 'error' ? `hiba: ${r.error}`
                      : Object.values(act).includes('mismatch') ? '⚠️ eltér a meglévőtől — nem írtuk felül'
                        : Object.values(act).includes('filled') ? '✅ kitöltve' : 'már megvolt, egyezik';
                  return (
                    <tr key={r.month}>
                      <td style={{ ...td, fontWeight: 600 }}>{r.month}</td>
                      <td style={td}>{r.perEur && r.perEur.HUF ? num(r.perEur.HUF, 2) : '—'}</td>
                      <td style={td}>{r.perEur && r.perEur.USD ? num(r.perEur.USD, 4) : '—'}</td>
                      <td style={{ ...td, color: /⚠️|hiba/.test(label) ? C.warn : C.muted }}>{label}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {fetched.problems && fetched.problems.length > 0 && (
              <div style={{ color: C.warn, fontSize: 11, marginTop: 8 }}>
                {fetched.problems.map((p, i) => <div key={i}>⚠️ {p}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      {/* InforEuro beillesztés — havonta ismétlődő feladat, gépelés nélkül */}
      <div style={{ ...card, background: C.bg, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>📋 InforEuro táblázat beillesztése</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.6 }}>
          Másold be az InforEuro oldaláról a táblázatot, a dátumokkal együtt. Az <b>EUR/HUF</b> és az
          <b> EUR/USD</b> két külön táblázat — illeszd be egymás után, a második nem törli az elsőt.
          Mentés előtt megmutatom, mit olvastam ki.
        </div>
        <textarea value={paste} onChange={(e) => { setPaste(e.target.value); setPreview(null); }}
          placeholder={'01/09/2026\t30/09/2026\tEUR/HUF\t364.79\t1 EUR is equal to 364.79 HUF'}
          style={{ ...input, minHeight: 90, fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => runImport(false)} disabled={importing || !paste.trim()} style={btn(false)}>
            {importing ? '⏳' : 'Előnézet'}
          </button>
          {preview && (
            <button onClick={() => runImport(true)} disabled={importing} style={btn(true)}>
              Mentés ({preview.wouldWrite} hónap)
            </button>
          )}
        </div>
        {preview && (
          <div style={{ marginTop: 10 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>{['Hónap', 'Beolvasva', '1 USD = EUR', '1 HUF = EUR'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {preview.preview.map((r) => (
                  <tr key={r.month}>
                    <td style={{ ...td, fontWeight: 600 }}>{r.month}</td>
                    <td style={{ ...td, color: C.muted }}>{r.seen.join(' · ')}</td>
                    <td style={td}>{r.usdEur == null ? '—' : num(r.usdEur, 4)}</td>
                    <td style={td}>{r.hufEur == null ? '—' : num(r.hufEur, 6)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.problems && preview.problems.length > 0 && (
              <div style={{ color: C.warn, fontSize: 11, marginTop: 8 }}>
                {preview.problems.map((p, i) => <div key={i}>⚠️ {p}</div>)}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Hónap</div>
          <input value={f.month} onChange={set('month')} placeholder="2026-08" style={input} />
        </label>
        <label style={{ flex: '0 0 170px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Forrás</div>
          <select value={f.source} onChange={set('source')} style={input}>
            {Object.entries(SOURCE_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Kapott összeg</div>
          <input type="number" step="0.01" value={f.amount} onChange={set('amount')} placeholder="32632" style={input} />
        </label>
        <label style={{ flex: '0 0 90px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Pénznem</div>
          <select value={f.currency} onChange={set('currency')} style={input}>
            {['HUF', 'EUR', 'USD'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Számlaszám</div>
          <input value={f.invoice_ref} onChange={set('invoice_ref')} placeholder="E-LE-2026-17" style={input} />
        </label>
        <button onClick={submit} disabled={saving || !f.month || f.amount === ''} style={btn(true)}>Mentés</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Hónap', 'Forrás', 'Kapott összeg', 'Számlaszám', 'Megjegyzés', ''].map((h) => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {d.revenueActuals.map((r) => (
            <tr key={r.id}>
              <td style={{ ...td, fontWeight: 600 }}>{r.month}</td>
              <td style={td}>{SOURCE_LABELS[r.source] || r.source}</td>
              <td style={td}>{num(r.amount, 2)} {r.currency}</td>
              <td style={{ ...td, color: r.invoice_ref ? C.text : C.muted }}>{r.invoice_ref || '—'}</td>
              <td style={{ ...td, color: C.muted, whiteSpace: 'normal' }}>{r.note || ''}</td>
              <td style={td}>
                <button onClick={() => window.confirm(`Törlöd a ${r.month} / ${r.source} sort?`) && send(`/revenue-actuals/${r.id}`, null, 'DELETE')}
                  style={{ ...btn(false), padding: '4px 8px', color: C.bad }}>×</button>
              </td>
            </tr>
          ))}
          {!d.revenueActuals.length && (
            <tr><td colSpan={6} style={{ ...td, color: C.warn }}>
              Nincs felvive tényleges kifizetés — a nettó bevétel egyelőre feltételezett jutalékkulcson áll.
            </td></tr>
          )}
        </tbody>
      </table>

      {/* Tény vs. modell eltérés — ez árulja el, ha a jutalékkulcs vagy a bolti adat rossz */}
      {d.series.some((s) => s.revenueActualVsModelledEur != null) && (
        <>
          <div style={{ fontWeight: 700, fontSize: 13, margin: '18px 0 6px' }}>Tény vs. modell</div>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.6 }}>
            Ahol a kettő érdemben elválik, az vagy rossz jutalékkulcs, vagy hiányos bolti adat.
            Beadás előtt ezt meg kell találni.
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Hónap', 'Modellezett nettó', 'Tényleges', 'Eltérés', 'Alap'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {d.series.filter((s) => s.revenueActualVsModelledEur != null).map((s) => {
                const big = s.revenueModelledNetEur > 0
                  && Math.abs(s.revenueActualVsModelledEur) > s.revenueModelledNetEur * 0.1;
                return (
                  <tr key={s.month}>
                    <td style={{ ...td, fontWeight: 600 }}>{s.month}</td>
                    <td style={td}>{eur(s.revenueModelledNetEur, 2)}</td>
                    <td style={td}>{eur(s.revenueActualNetEur, 2)}</td>
                    <td style={{ ...td, color: big ? C.warn : C.muted, fontWeight: big ? 600 : 400 }}>
                      {eur(s.revenueActualVsModelledEur, 2)}{big && ' ⚠️'}
                    </td>
                    <td style={{ ...td, color: C.muted }}>
                      {{ actual: 'tény', modelled: 'modell', mixed: 'vegyes' }[s.revenueNetBasis]}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

// ── Fix költségek ──────────────────────────────────────────────────────────
const CAT_LABELS = {
  personnel: 'Személyi', services: 'Szolgáltatás', tools: 'Eszköz/szoftver',
  office: 'Iroda', marketing: 'Marketing', other: 'Egyéb',
};
const PERIOD_LABELS = { monthly: 'havi', annual: 'éves', one_off: 'egyszeri' };

function FixedTab({ d, send, saving }) {
  const empty = { label: '', category: 'services', amount: '', currency: 'EUR', period: 'monthly', start_month: '', end_month: '', note: '' };
  const [f, setF] = useState(empty);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    const ok = await send('/fixed-costs', {
      ...f, amount: Number(f.amount),
      start_month: f.start_month || null, end_month: f.end_month || null,
    });
    if (ok) setF(empty);
  };

  return (
    <>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.7 }}>
        Amit a dashboard <b>nem lát</b>: könyvelő, bankköltség, Apple (99 USD/év) és Google Play
        (25 USD egyszeri) fejlesztői díj, domain, tárhely, tulajdonosi kivét, adó. Enélkül a mért burn
        csak az AI- és szerverköltséget tartalmazza, és jóval a bankból látszó fogyás alatt marad.
        <br />
        Az éves tételt 12-vel osztva számoljuk el havonta; az egyszeri tétel csak a saját hónapjába kerül.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ flex: '1 1 180px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Megnevezés</div>
          <input value={f.label} onChange={set('label')} placeholder="Könyvelő" style={input} />
        </label>
        <label style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Kategória</div>
          <select value={f.category} onChange={set('category')} style={input}>
            {Object.entries(CAT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Összeg</div>
          <input type="number" step="0.01" value={f.amount} onChange={set('amount')} style={input} />
        </label>
        <label style={{ flex: '0 0 85px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Pénznem</div>
          <select value={f.currency} onChange={set('currency')} style={input}>
            {['EUR', 'HUF', 'USD'].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 0 100px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Időszak</div>
          <select value={f.period} onChange={set('period')} style={input}>
            {Object.entries(PERIOD_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
            Tól {f.period === 'one_off' && <span style={{ color: C.warn }}>*</span>}
          </div>
          <input value={f.start_month} onChange={set('start_month')} placeholder="2026-04" style={input} />
        </label>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Ig</div>
          <input value={f.end_month} onChange={set('end_month')} placeholder="" style={input} />
        </label>
        <button onClick={submit} disabled={saving || !f.label || f.amount === ''} style={btn(true)}>Hozzáadás</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Megnevezés', 'Kategória', 'Összeg', 'Időszak', 'Tól', 'Ig', ''].map((h) => <th key={h} style={th}>{h}</th>)}
        </tr></thead>
        <tbody>
          {d.fixedCosts.map((r) => (
            <tr key={r.id}>
              <td style={{ ...td, fontWeight: 600 }}>{r.label}</td>
              <td style={{ ...td, color: C.muted }}>{CAT_LABELS[r.category] || r.category}</td>
              <td style={td}>{num(r.amount, 2)} {r.currency}</td>
              <td style={{ ...td, color: C.muted }}>{PERIOD_LABELS[r.period] || r.period}</td>
              <td style={{ ...td, color: C.muted }}>{r.start_month || '—'}</td>
              <td style={{ ...td, color: C.muted }}>{r.end_month || 'ma is él'}</td>
              <td style={td}>
                <button onClick={() => window.confirm(`Törlöd: ${r.label}?`) && send(`/fixed-costs/${r.id}`, null, 'DELETE')}
                  style={{ ...btn(false), padding: '4px 8px', color: C.bad }}>×</button>
              </td>
            </tr>
          ))}
          {!d.fixedCosts.length && <tr><td colSpan={7} style={{ ...td, color: C.warn }}>Nincs fix költség — a mért burn emiatt jóval a valóság alatt van.</td></tr>}
        </tbody>
      </table>
    </>
  );
}

// ── Árfolyam ───────────────────────────────────────────────────────────────
function FxTab({ d, send, saving, load }) {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ month: '', usd_eur: '', huf_eur: '', source: 'InforEuro' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const loadRows = useCallback(async () => {
    const r = await fetch('/api/finance/fx', { credentials: 'include' });
    setRows(await r.json());
  }, []);
  useEffect(() => { loadRows(); }, [loadRows, d]);

  const submit = async () => {
    const ok = await send('/fx', f);
    if (ok) { setF({ month: '', usd_eur: '', huf_eur: '', source: f.source }); loadRows(); }
  };

  // InforEuro letöltés a Bizottság API-járól. Csak üres helyre ír; a meglévő
  //   értéket, ha eltér a hivatalostól, jelzi, de nem írja felül.
  const [fetching, setFetching] = useState(false);
  const [fetched, setFetched] = useState(null);
  const fetchOfficial = async () => {
    setFetching(true); setFetched(null);
    try {
      const r = await fetch('/api/finance/fx/fetch-inforeuro', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: '{}',
      });
      const j = await r.json();
      setFetched(r.ok ? j : { error: j.error || 'HTTP ' + r.status });
      await loadRows(); await load();
    } catch (e) { setFetched({ error: e.message }); } finally { setFetching(false); }
  };

  // InforEuro beillesztés: előbb előnézet, csak utána mentés. Egy fordítva
  //   értelmezett árfolyam némán elrontaná az összes euróban megadott számot.
  const [paste, setPaste] = useState('');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const runImport = async (save) => {
    setImporting(true);
    try {
      const r = await fetch('/api/finance/fx/import', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: paste, preview: !save }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'HTTP ' + r.status);
      if (save) { setPreview(null); setPaste(''); await loadRows(); await load(); }
      else setPreview(j);
    } catch (e) { alert('Hiba: ' + e.message); } finally { setImporting(false); }
  };

  return (
    <>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.7 }}>
        Pályázati elszámolásban nem a napi piaci árfolyam a védhető, hanem a Bizottság havi
        <b> InforEuro</b> árfolyama (vagy az EKB referencia-árfolyama). Vidd fel havonta, a forrás
        megnevezésével — az export módszertani lapjára is ez kerül.
        <br />
        A HUF→EUR mezőbe azt írd, hogy <b>1 forint hány euró</b> (pl. 400 Ft/EUR esetén 0,0025).
        Ha egy hónapra nincs árfolyam, a legközelebbi korábbit használjuk, és a havi bontásban jelezzük.
      </div>

      <div style={{
        ...card, background: C.bg, marginBottom: 14,
        borderColor: d.fx.usingManual ? C.ok : C.warn,
      }}>
        <div style={{ fontSize: 12, color: d.fx.usingManual ? C.ok : C.warn, fontWeight: 600 }}>
          {d.fx.usingManual ? '✅' : '⚠️'} {d.fx.note}
        </div>
        {d.fx.partialMonths && d.fx.partialMonths.length > 0 && (
          <div style={{ fontSize: 11, color: C.warn, marginTop: 6 }}>
            Ezekben a hónapokban csak az egyik árfolyam kézi, a másikat a napi automata pótolja:{' '}
            {d.fx.partialMonths.join(', ')}
          </div>
        )}
        {d.fx.autoFallback && (
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Automata tartalék ({d.fx.autoFallback.date}): 1 USD = {num(d.fx.autoFallback.usdEur, 4)} EUR ·
            {' '}1 HUF = {num(d.fx.autoFallback.hufEur, 6)} EUR
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <label style={{ flex: '0 0 110px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Hónap</div>
          <input value={f.month} onChange={set('month')} placeholder="2026-08" style={input} />
        </label>
        <label style={{ flex: '0 0 130px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>1 USD = ? EUR</div>
          <input type="number" step="0.0001" value={f.usd_eur} onChange={set('usd_eur')} placeholder="0.9000" style={input} />
        </label>
        <label style={{ flex: '0 0 150px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>1 HUF = ? EUR</div>
          <input type="number" step="0.000001" value={f.huf_eur} onChange={set('huf_eur')} placeholder="0.002500" style={input} />
        </label>
        <label style={{ flex: '1 1 160px' }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Forrás</div>
          <input value={f.source} onChange={set('source')} style={input} />
        </label>
        <button onClick={submit} disabled={saving || !f.month} style={btn(true)}>Mentés</button>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Hónap', '1 USD = EUR', '1 HUF = EUR', 'Forrás', ''].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {(rows?.monthly || []).map((r) => (
            <tr key={r.month}>
              <td style={{ ...td, fontWeight: 600 }}>{r.month}</td>
              <td style={td}>{r.usd_eur == null ? '—' : num(r.usd_eur, 4)}</td>
              <td style={td}>{r.huf_eur == null ? '—' : num(r.huf_eur, 6)}</td>
              <td style={{ ...td, color: C.muted }}>{r.source}</td>
              <td style={td}>
                <button onClick={() => window.confirm(`Törlöd a ${r.month} árfolyamot?`) && send(`/fx/${r.month}`, null, 'DELETE').then(loadRows)}
                  style={{ ...btn(false), padding: '4px 8px', color: C.bad }}>×</button>
              </td>
            </tr>
          ))}
          {rows && !rows.monthly.length && <tr><td colSpan={5} style={{ ...td, color: C.warn }}>Nincs kézi árfolyam — a napi piaci árfolyammal számolunk.</td></tr>}
        </tbody>
      </table>
    </>
  );
}
